import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react'
import { createPortal } from 'react-dom'
import { useLang } from '../i18n'
import { parseRoute, tourShareUrl, usePath } from '../lib/router'
import { useAvailableTours } from '../tours/registry'
import { useTour } from '../tours/TourRunner'
import { recentTours, toCatalogEntry } from '../tours/catalog'
import { clampToViewport, type Point } from '../tours/placement'
import { TOUR_DEFS } from '../tours/defs'
import type { TourDef } from '../tours/types'
import { useTourLink } from '../tours/deepLink'
import { useTourRecords } from '../userhub/training'
import { ToursCatalog } from '../userhub/ToursCatalog'

/** How long the "Link copied" status stays up. */
const TOAST_MS = 3000

/** Fills `{name}` placeholders of a `gt_*` string. */
function fill(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m))
}

interface GuidedToursWindowProps {
  onClose: () => void
  /** The foot link "All guided tours (n)": opens the userhub catalog. */
  onOpenCatalog: () => void
  /** The registered tours (tests pass fakes); the window lists those available to the user. */
  tours?: readonly TourDef[]
}

/**
 * The small Guided tours window, opened by Help → Guided tours (docs/spec/62-help.md): floating,
 * non-modal (no overlay, no focus trap — the page stays usable), draggable by its title bar.
 *
 *  - **For this screen**: the tours whose `scope.views` include the current view (hidden when none);
 *  - **Recent**: the 5 latest by `publishedOn`.
 *
 * A row: `#n` · title · "≈ n min" · status (New / Completed / Updated, from userhub — a down
 * userhub reads as New) · Start, or Redo once completed · Copy link. Start starts the tour and
 * closes the window. The foot link opens the full userhub catalog.
 */
export function GuidedToursWindow({ onClose, onOpenCatalog, tours: defs = TOUR_DEFS }: GuidedToursWindowProps) {
  const { t, lang } = useLang()
  const tours = useAvailableTours(defs)
  const records = useTourRecords()
  const tour = useTour()
  const { view } = parseRoute(usePath())
  const uid = useId()
  const titleId = `${uid}-title`

  const forScreen = tours.filter((d) => d.scope.views?.includes(view))
  const recent = recentTours(tours)

  const rootRef = useRef<HTMLDivElement | null>(null)
  const returnFocusRef = useRef<Element | null>(null)
  useEffect(() => {
    returnFocusRef.current = document.activeElement
    rootRef.current?.focus({ preventScroll: true })
  }, [])
  const close = (): void => {
    onClose()
    const back = returnFocusRef.current
    if (back instanceof HTMLElement && back.isConnected) back.focus()
  }

  // The status line for Copy link, cleared after a few seconds.
  const [toast, setToast] = useState('')
  useEffect(() => {
    if (!toast) return
    const timer = setTimeout(() => setToast(''), TOAST_MS)
    return () => clearTimeout(timer)
  }, [toast])
  const copyLink = (n: number): void => {
    const done = () => setToast(fill(t('gt_link_copied'), { n }))
    const failed = () => setToast(t('gt_link_copy_failed'))
    try {
      void navigator.clipboard.writeText(tourShareUrl(n)).then(done, failed)
    } catch {
      failed()
    }
  }

  const start = (d: TourDef): void => {
    onClose()
    tour.start(d.id)
  }

  // Dragged by its title bar; null = its default place (CSS: top right, under the top bar).
  const [pos, setPos] = useState<Point | null>(null)
  const dragRef = useRef<{ px: number; py: number; x: number; y: number } | null>(null)
  const onDragStart = (e: PointerEvent<HTMLDivElement>): void => {
    if (e.button !== 0 || (e.target as Element).closest('button')) return
    const at = rootRef.current?.getBoundingClientRect()
    if (!at) return
    e.currentTarget.setPointerCapture?.(e.pointerId)
    dragRef.current = { px: e.clientX, py: e.clientY, x: at.left, y: at.top }
  }
  const onDragMove = (e: PointerEvent<HTMLDivElement>): void => {
    const o = dragRef.current
    const el = rootRef.current
    if (!o || !el) return
    const size = { w: el.offsetWidth, h: el.offsetHeight }
    setPos(clampToViewport({ x: o.x + e.clientX - o.px, y: o.y + e.clientY - o.py }, size, {
      width: window.innerWidth, height: window.innerHeight,
    }))
  }
  const onDragEnd = (): void => { dragRef.current = null }

  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.key !== 'Escape') return
    e.preventDefault()
    e.stopPropagation()
    close()
  }

  const row = (d: TourDef, section: string) => {
    const status = records.status(toCatalogEntry(d))
    const title = d.title[lang]
    const done = status !== 'new'
    return (
      <li key={`${section}-${d.id}`} className="gt-row" data-testid={`gt-row-${section}-${d.number}`}>
        <span className="gt-num">#{d.number}</span>
        <span className="gt-name">{title}</span>
        <span className="gt-meta">
          <span>{fill(t('gt_minutes'), { n: d.minutes })}</span>
          <span className="gt-status" data-testid="gt-status">
            {t(status === 'completed' ? 'gt_status_completed' : status === 'updated' ? 'gt_status_updated' : 'gt_status_new')}
          </span>
        </span>
        <span className="gt-acts">
          <button
            type="button"
            className="btn gt-copy"
            aria-label={fill(t('gt_copy_link_label'), { n: d.number })}
            title={t('gt_copy_link')}
            onClick={() => copyLink(d.number)}
          >
            {t('gt_copy_link')}
          </button>
          <button
            type="button"
            className="btn primary"
            aria-label={fill(t(done ? 'gt_redo_label' : 'gt_start_label'), { n: d.number, title })}
            onClick={() => start(d)}
          >
            {t(done ? 'gt_redo' : 'gt_start')}
          </button>
        </span>
      </li>
    )
  }

  return createPortal(
    <div
      ref={rootRef}
      className="gt-window"
      role="dialog"
      aria-labelledby={titleId}
      tabIndex={-1}
      data-testid="guided-tours-window"
      style={pos ? { left: pos.x, top: pos.y, right: 'auto' } : undefined}
      onKeyDown={onKeyDown}
    >
      <div
        className="gt-titlebar"
        title={t('gt_move_hint')}
        onPointerDown={onDragStart}
        onPointerMove={onDragMove}
        onPointerUp={onDragEnd}
        onPointerCancel={onDragEnd}
      >
        <h2 className="gt-title" id={titleId}>{t('gt_title')}</h2>
        <button type="button" className="gt-x" aria-label={t('gt_close')} title={t('gt_close')} onClick={close}>
          <span aria-hidden="true">×</span>
        </button>
      </div>
      <div className="gt-body">
        {forScreen.length > 0 && (
          <section className="gt-section" aria-labelledby={`${uid}-screen`}>
            <h3 className="gt-heading" id={`${uid}-screen`}>{t('gt_for_this_screen')}</h3>
            <ul className="gt-list">{forScreen.map((d) => row(d, 'screen'))}</ul>
          </section>
        )}
        <section className="gt-section" aria-labelledby={`${uid}-recent`}>
          <h3 className="gt-heading" id={`${uid}-recent`}>{t('gt_recent')}</h3>
          <ul className="gt-list">{recent.map((d) => row(d, 'recent'))}</ul>
        </section>
      </div>
      <div className="gt-foot">
        <button
          type="button"
          className="gt-all"
          onClick={() => { onClose(); onOpenCatalog() }}
          data-testid="gt-all"
        >
          {fill(t('gt_all'), { n: tours.length })}
        </button>
        <p className="gt-toast" role="status">{toast}</p>
      </div>
    </div>,
    document.body,
  )
}

interface GuidedToursLayerProps {
  windowOpen: boolean
  onCloseWindow: () => void
  /** The registered tours (tests pass fakes). */
  tours?: readonly TourDef[]
}

/**
 * The Guided tours window and the userhub catalog, over whichever shell is up (App.tsx). A tour
 * link (`/tour/<n>`, `?tour=<n>`, `niobe://tour/<n>`) opens the catalog on tour #n, its Start
 * focused — it never starts the tour.
 */
export function GuidedToursLayer({ windowOpen, onCloseWindow, tours: defs = TOUR_DEFS }: GuidedToursLayerProps) {
  const tour = useTour()
  const tours = useAvailableTours(defs)
  const [catalog, setCatalog] = useState<{ open: boolean; selected: number | null }>({ open: false, selected: null })
  const closeWindowRef = useRef(onCloseWindow)
  closeWindowRef.current = onCloseWindow
  useTourLink(useCallback((n: number) => {
    closeWindowRef.current()
    setCatalog({ open: true, selected: n })
  }, []))
  return (
    <>
      {windowOpen && (
        <GuidedToursWindow
          tours={defs}
          onClose={onCloseWindow}
          onOpenCatalog={() => setCatalog({ open: true, selected: null })}
        />
      )}
      <ToursCatalog
        open={catalog.open}
        onClose={() => setCatalog({ open: false, selected: null })}
        tours={tours}
        selectedNumber={catalog.selected}
        onStartTour={(def) => {
          setCatalog({ open: false, selected: null })
          tour.start(def.id)
        }}
      />
    </>
  )
}
